/**
 * The Brand and Messaging Profile's fields, described once.
 *
 * BCE staff and the client's own admins edit the same profile from two
 * different apps, and the client's members read it in a third view. The
 * explanations have to match everywhere or the two sides end up filling in
 * different things — so labels, descriptions and control kinds live here
 * rather than in either app.
 *
 * Non-UI shared code in this package has precedent: see `sign-in-routing.ts`.
 *
 * Field meanings follow §5 of the Phase One requirements. Two are commonly
 * misread and their descriptions say so explicitly:
 *
 * - **Internal program names** is the *client's* names for their own
 *   initiatives, not a way to rename BCE's programs.
 * - **Company terminology** is vocabulary for BCE to use in program content.
 *   It is not the portal's UI-label override.
 */

/** Which record a field is stored on. The two save separately. */
export type ProfileSource = 'brand' | 'messaging';

/** How a field is edited. */
export type ProfileFieldKind =
  | 'text'
  | 'prose'
  | 'list'
  | 'rows'
  | 'color'
  | 'image'
  | 'font'
  | 'url';

/** One column of a repeatable-row field. */
export interface ProfileRowColumn {
  readonly key: string;
  readonly label: string;
  readonly placeholder?: string;
  /** Renders as a textarea rather than a single line. */
  readonly multiline?: boolean;
}

/** One editable field of the profile. */
export interface ProfileField {
  readonly key: string;
  readonly source: ProfileSource;
  readonly label: string;
  /** What this field is for, in the client's terms. Shown above the control. */
  readonly description: string;
  readonly kind: ProfileFieldKind;
  readonly placeholder?: string;
  /**
   * Preview this image against the sidebar color rather than the page
   * background — for artwork drawn to sit on the dark sidebar.
   */
  readonly previewOnSidebar?: boolean;
  /**
   * What this field is before a client changes it. Present on the visual
   * fields so an unwanted change can be put back — a client who has just
   * made their portal unreadable needs a way out that does not involve
   * remembering the original hex.
   */
  readonly defaultValue?: string;
  /** For `rows` fields: the shape of one row. */
  readonly columns?: readonly ProfileRowColumn[];
}

/** A titled group of fields. */
export interface ProfileSection {
  readonly key: string;
  readonly title: string;
  /** What the whole section is for. */
  readonly description: string;
  readonly fields: readonly ProfileField[];
}

export const BRAND_PROFILE_SECTIONS: readonly ProfileSection[] = [
  {
    key: 'identity',
    title: 'Visual identity',
    description:
      'How your portal looks to your people. Changes here apply immediately, including on the sign-in screen.',
    fields: [
      {
        key: 'logoUrl',
        source: 'brand',
        label: 'Logo',
        description:
          'Shown in the portal sidebar and on the sign-in screen. A transparent PNG or SVG works best.',
        kind: 'image',
        defaultValue: '',
      },
      {
        key: 'logoDarkUrl',
        source: 'brand',
        label: 'Logo for dark backgrounds',
        description:
          'Used where the logo sits on your sidebar color. Leave empty if your main logo already reads well on dark.',
        kind: 'image',
        previewOnSidebar: true,
        defaultValue: '',
      },
      {
        key: 'faviconUrl',
        source: 'brand',
        label: 'Favicon',
        description: 'The small icon browsers show on the tab.',
        kind: 'image',
        defaultValue: '',
      },
      {
        key: 'primaryColor',
        source: 'brand',
        label: 'Primary color',
        description:
          'Buttons, links and highlights. Use your main brand color: it needs to stay readable with white text on it.',
        kind: 'color',
        defaultValue: '#00338d',
      },
      {
        key: 'sidebarColor',
        source: 'brand',
        label: 'Sidebar color',
        description: 'The navigation panel down the left. Usually a darker shade of your primary.',
        kind: 'color',
        defaultValue: '#001d52',
      },
      {
        key: 'accentColor',
        source: 'brand',
        label: 'Accent color',
        description: 'Small highlights, such as the marker on the active navigation item.',
        kind: 'color',
        defaultValue: '#5ec4ff',
      },
      {
        key: 'fontFamily',
        source: 'brand',
        label: 'Font',
        description:
          'Used everywhere in your portal, including the sign-in screen. System fonts are already on your people’s machines; the others are fetched when the portal loads.',
        kind: 'font',
        defaultValue: 'Arial, Helvetica, sans-serif',
      },
    ],
  },
  {
    key: 'signin',
    title: 'Sign-in screen',
    description: 'The first thing your people see. This is the one page shown before anyone signs in.',
    fields: [
      {
        key: 'heroImageUrl',
        source: 'brand',
        label: 'Hero image',
        description: 'The large image beside the sign-in form. Landscape, at least 1600px wide.',
        kind: 'image',
        defaultValue: '',
      },
      {
        key: 'loginEyebrow',
        source: 'brand',
        label: 'Eyebrow',
        description: 'The small line of text above the headline.',
        kind: 'text',
        placeholder: 'TEAM EXPERIENCES',
        defaultValue: '',
      },
      {
        key: 'loginHeadline',
        source: 'brand',
        label: 'Headline',
        description: 'The large greeting on the sign-in screen.',
        kind: 'text',
        placeholder: 'Welcome back',
        defaultValue: '',
      },
      {
        key: 'loginSubcopy',
        source: 'brand',
        label: 'Sub-copy',
        description: 'A sentence under the headline explaining what this portal is for.',
        kind: 'prose',
        defaultValue: '',
      },
      {
        key: 'heroQuote',
        source: 'brand',
        label: 'Hero quote',
        description:
          'A short quote laid over the hero image, often a line from a leader about why this matters.',
        kind: 'prose',
        defaultValue: '',
      },
      {
        key: 'heroStatValue',
        source: 'brand',
        label: 'Hero statistic',
        description: 'A single number worth showing off, e.g. "2,340".',
        kind: 'text',
        defaultValue: '',
      },
      {
        key: 'heroStatLabel',
        source: 'brand',
        label: 'Hero statistic label',
        description: 'What that number counts, e.g. "colleagues took part last year".',
        kind: 'text',
        defaultValue: '',
      },
      {
        key: 'welcomeMessage',
        source: 'brand',
        label: 'Welcome message',
        description: 'Shown on the dashboard after someone signs in.',
        kind: 'prose',
        defaultValue: '',
      },
    ],
  },
  {
    key: 'purpose',
    title: 'Purpose',
    description:
      'Why your organization exists. BCE opens and closes programs with this, so it should read the way your leadership says it.',
    fields: [
      {
        key: 'mission',
        source: 'messaging',
        label: 'Mission',
        description: 'What you are here to do, in your own published wording.',
        kind: 'prose',
      },
      {
        key: 'vision',
        source: 'messaging',
        label: 'Vision',
        description: 'Where you are trying to get to.',
        kind: 'prose',
      },
      {
        key: 'futureGoals',
        source: 'messaging',
        label: 'Future goals and aspirations',
        description:
          'What the next few years look like if things go well. Useful for closing remarks and awards.',
        kind: 'prose',
      },
    ],
  },
  {
    key: 'values',
    title: 'Values and culture',
    description:
      'The language your people already recognize. BCE builds challenges, discussion questions and team materials around these. One per line.',
    fields: [
      {
        key: 'coreValues',
        source: 'messaging',
        label: 'Core values',
        description: 'Your published values, exactly as your people would recognize them.',
        kind: 'list',
        placeholder: 'Integrity\nExcellence\nTogether',
      },
      {
        key: 'cultureStatements',
        source: 'messaging',
        label: 'Culture statements',
        description:
          'How it actually feels to work there: the things you say about yourselves internally.',
        kind: 'list',
        placeholder: 'We speak up, whoever is in the room',
      },
      {
        key: 'leadershipPrinciples',
        source: 'messaging',
        label: 'Leadership principles',
        description: 'What you expect of people who lead others.',
        kind: 'list',
      },
      {
        key: 'strategicPriorities',
        source: 'messaging',
        label: 'Strategic priorities',
        description: 'What the organization is focused on this year.',
        kind: 'list',
      },
      {
        key: 'currentInitiatives',
        source: 'messaging',
        label: 'Current initiatives',
        description:
          'Programs of work under way right now that an event might reasonably nod to.',
        kind: 'list',
      },
      {
        key: 'customerServicePrinciples',
        source: 'messaging',
        label: 'Customer-service principles',
        description: 'How you expect people to treat customers.',
        kind: 'list',
      },
      {
        key: 'desiredBehaviors',
        source: 'messaging',
        label: 'Desired behaviors',
        description:
          'The behaviors you are actively trying to encourage, often the real objective behind booking an event.',
        kind: 'list',
      },
      {
        key: 'csrPriorities',
        source: 'messaging',
        label: 'CSR priorities',
        description:
          'The causes you support. BCE matches charitable programs and beneficiaries to these.',
        kind: 'list',
        placeholder: 'Education and social mobility\nChildren’s wellbeing',
      },
    ],
  },
  {
    key: 'vocabulary',
    title: 'How you talk',
    description:
      'Your in-house language, so BCE-written content sounds like it came from inside your organization rather than from a vendor.',
    fields: [
      {
        key: 'internalProgramNames',
        source: 'messaging',
        label: 'Internal program names',
        description:
          'Your own names for your internal programs and initiatives, such as a leadership program you call “Cornerstone”, a safety push called “Zero Harm”. BCE references these in trivia, challenges and slides. This is not about renaming BCE’s programs.',
        kind: 'list',
        placeholder: 'Cornerstone Week\nZero Harm',
      },
      {
        key: 'companyTerminology',
        source: 'messaging',
        label: 'Company terminology',
        description:
          'Words your people use instead of the obvious ones, and what they mean. BCE uses your vocabulary in program content.',
        kind: 'rows',
        columns: [
          { key: 'term', label: 'Your word', placeholder: 'Crew' },
          { key: 'meaning', label: 'What it means', placeholder: 'Employees' },
        ],
      },
    ],
  },
  {
    key: 'leadership',
    title: 'Leadership voice',
    description:
      'Approved messages BCE may quote on a slide or in opening remarks. Attribution matters: anything used gets shown with a name.',
    fields: [
      {
        key: 'leadershipMessages',
        source: 'messaging',
        label: 'Approved leadership messages',
        description:
          'Quotable lines from named leaders. Only add what you are happy to see on a screen in front of the whole room.',
        kind: 'rows',
        columns: [
          { key: 'author', label: 'Who said it', placeholder: 'Dana Whitfield' },
          { key: 'title', label: 'Their title', placeholder: 'Chief People Officer' },
          { key: 'message', label: 'The message', multiline: true },
        ],
      },
    ],
  },
  {
    key: 'materials',
    title: 'Approved materials',
    description: 'Where BCE should go for your artwork and the rules that govern it.',
    fields: [
      {
        key: 'brandGuidelinesUrl',
        source: 'messaging',
        label: 'Brand guidelines',
        description:
          'A link to your brand guidelines document, so BCE designers work from your rules rather than guessing.',
        kind: 'url',
        placeholder: 'https://…',
      },
      {
        key: 'approvedAssets',
        source: 'messaging',
        label: 'Approved graphics and images',
        description:
          'Artwork BCE may use, as links. Anything not listed here will be checked with you first.',
        kind: 'rows',
        columns: [
          { key: 'label', label: 'What it is', placeholder: 'Logo pack' },
          { key: 'url', label: 'Link', placeholder: 'https://…' },
        ],
      },
    ],
  },
] as const;

/** Every field, flattened — for lookups and for building a save payload. */
export const BRAND_PROFILE_FIELDS: readonly ProfileField[] = BRAND_PROFILE_SECTIONS.flatMap(
  (section) => section.fields,
);

/**
 * The fields stored on one of the two records.
 *
 * @param source - Which record.
 * @returns Its fields, in display order.
 */
export function fieldsFor(source: ProfileSource): readonly ProfileField[] {
  return BRAND_PROFILE_FIELDS.filter((field) => field.source === source);
}
